/**
 * pdf-doc-helpers.js — pure-JS PDF document helpers shared by every
 * pdf-lib-based tool worker (merge-pdf, split-pdf, future
 * extract-pages / remove-pages / reorder-pages).
 *
 * Loaded via importScripts AFTER pdf-lib. Exposes self.PdfDocHelpers
 * with named functions. parsePageRange is exported even when pdf-lib
 * is missing (it has no pdf-lib deps and is the most-tested helper).
 *
 * If you ever change one of these functions, run BOTH worker test
 * suites — they share the implementation.
 */
(function (global) {
    'use strict';

    /**
     * Parse a 1-indexed page-range string into 0-indexed indices.
     * Accepts: '', 'all', '1-end', '1,3,5', '1-3,7,9-end', '2-2'.
     * Out-of-bounds or malformed throws RangeError('BAD_RANGE: <reason>').
     */
    function parsePageRange(range, pageCount) {
        if (pageCount <= 0) return [];
        const s = String(range || '').trim().toLowerCase();
        if (!s || s === 'all' || s === '1-end') {
            return Array.from({ length: pageCount }, (_, i) => i);
        }
        const out = new Set();
        const parts = s.split(',').map(p => p.trim()).filter(Boolean);
        for (const part of parts) {
            const m = part.match(/^(\d+|end)(?:\s*-\s*(\d+|end))?$/);
            if (!m) throw new RangeError(`BAD_RANGE: unrecognised segment "${part}"`);
            const a = m[1] === 'end' ? pageCount : parseInt(m[1], 10);
            const b = m[2] == null ? a : (m[2] === 'end' ? pageCount : parseInt(m[2], 10));
            if (!Number.isFinite(a) || !Number.isFinite(b) || a < 1 || b < 1) {
                throw new RangeError(`BAD_RANGE: "${part}" out of range`);
            }
            if (a > pageCount || b > pageCount) {
                throw new RangeError(`BAD_RANGE: "${part}" exceeds page count ${pageCount}`);
            }
            const lo = Math.min(a, b);
            const hi = Math.max(a, b);
            for (let i = lo; i <= hi; i++) out.add(i - 1);
        }
        return Array.from(out).sort((x, y) => x - y);
    }

    global.PdfDocHelpers = { parsePageRange };

    // Everything below depends on pdf-lib (PL). In unit-test sandboxes where
    // PDFLib is not stubbed we stop here — parsePageRange is enough.
    if (typeof global.PDFLib === 'undefined') return;

    const PL = global.PDFLib;

    function decodeTitle(raw) {
        if (!raw) return '';
        try {
            if (typeof raw.decodeText === 'function') return raw.decodeText();
            if (typeof raw.asString === 'function') return raw.asString();
            return String(raw);
        } catch { return ''; }
    }

    function stripLeadingSlash(s) {
        return s && s.charAt(0) === '/' ? s.slice(1) : s;
    }

    /**
     * Read a source PDF's full outline tree, resolving named destinations
     * via catalog /Dests (old style) and /Names /Dests (new-style flat
     * tree only — nested Kids arrays not supported).
     *
     * Returns: [{ title, pageIndex, children: [...] }] tree, all
     * pageIndex values are 0-based source-relative; -1 means unresolved.
     */
    function readSourceOutlines(srcDoc) {
        const out = [];
        let outlinesRef;
        try { outlinesRef = srcDoc.catalog.get(PL.PDFName.of('Outlines')); } catch { return out; }
        if (!outlinesRef) return out;

        const ctx = srcDoc.context;
        const pageRefToIndex = new Map();
        srcDoc.getPages().forEach((p, i) => pageRefToIndex.set(String(p.ref), i));

        const namedDests = new Map();
        try {
            const oldDestsRef = srcDoc.catalog.get(PL.PDFName.of('Dests'));
            if (oldDestsRef) {
                const dict = oldDestsRef.entries ? oldDestsRef : ctx.lookup(oldDestsRef);
                for (const [k, v] of dict.entries()) {
                    namedDests.set(stripLeadingSlash(String(k)), v);
                }
            }
        } catch {}
        try {
            const namesRef = srcDoc.catalog.get(PL.PDFName.of('Names'));
            if (namesRef) {
                const names = namesRef.entries ? namesRef : ctx.lookup(namesRef);
                const destsTreeRef = names.get(PL.PDFName.of('Dests'));
                if (destsTreeRef) {
                    const tree = destsTreeRef.entries ? destsTreeRef : ctx.lookup(destsTreeRef);
                    const namesArr = tree.get(PL.PDFName.of('Names'));
                    if (namesArr && namesArr.array) {
                        for (let i = 0; i + 1 < namesArr.array.length; i += 2) {
                            const key = namesArr.array[i];
                            const val = namesArr.array[i + 1];
                            const keyStr = key.decodeText ? key.decodeText() : String(key);
                            namedDests.set(stripLeadingSlash(keyStr), val);
                        }
                    }
                }
            }
        } catch {}

        function pageIndexFromDest(dest) {
            if (!dest) return -1;
            let cur = dest;
            try { if (cur && !cur.array && !cur.entries && cur.context) cur = ctx.lookup(cur); } catch {}
            if (cur && (cur.encodedName != null || cur.value != null) && !cur.array && !cur.entries) {
                const keyStr = stripLeadingSlash(cur.decodeText ? cur.decodeText() : String(cur));
                const resolved = namedDests.get(keyStr);
                if (!resolved) return -1;
                cur = resolved;
                try { if (cur && !cur.array && !cur.entries && cur.context) cur = ctx.lookup(cur); } catch {}
            }
            if (cur && cur.entries && !cur.array) {
                const inner = cur.get(PL.PDFName.of('D'));
                if (inner) {
                    cur = inner;
                    try { if (cur && !cur.array && !cur.entries && cur.context) cur = ctx.lookup(cur); } catch {}
                }
            }
            if (cur && cur.array) {
                const first = cur.array[0];
                if (first && pageRefToIndex.has(String(first))) return pageRefToIndex.get(String(first));
            }
            return -1;
        }

        function visit(itemRef, depth) {
            if (depth > 12) return [];
            const siblings = [];
            let cur;
            try { cur = ctx.lookup(itemRef); } catch { return siblings; }
            let safety = 0;
            while (cur && safety++ < 5000) {
                const title = decodeTitle(cur.get && cur.get(PL.PDFName.of('Title')));
                let dest = cur.get && cur.get(PL.PDFName.of('Dest'));
                if (!dest) {
                    const action = cur.get && cur.get(PL.PDFName.of('A'));
                    if (action) {
                        try {
                            const aDict = action.entries ? action : ctx.lookup(action);
                            const s = aDict.get(PL.PDFName.of('S'));
                            const isGoto = s && (s.encodedName === '/GoTo' || String(s) === '/GoTo');
                            if (isGoto) dest = aDict.get(PL.PDFName.of('D'));
                        } catch {}
                    }
                }
                const idx = pageIndexFromDest(dest);
                const node = { title, pageIndex: idx, children: [] };

                const firstRef = cur.get && cur.get(PL.PDFName.of('First'));
                if (firstRef) node.children = visit(firstRef, depth + 1);

                if (title || node.children.length) siblings.push(node);

                const nextRef = cur.get && cur.get(PL.PDFName.of('Next'));
                if (!nextRef) break;
                try { cur = ctx.lookup(nextRef); } catch { break; }
            }
            return siblings;
        }

        try {
            const root = ctx.lookup(outlinesRef);
            const firstRef = root.get(PL.PDFName.of('First'));
            if (firstRef) {
                const tree = visit(firstRef, 0);
                for (const t of tree) out.push(t);
            }
        } catch {}
        return out;
    }

    /**
     * Recursively walk a source-relative outline tree and rewrite each
     * node's pageIndex via toMergedIdx(srcPageIndex) → mergedPageIndex|null.
     * Nodes that don't resolve are dropped but their resolving descendants
     * are lifted to take the dropped node's slot.
     */
    function remapOutlineTree(srcTree, toMergedIdx) {
        const out = [];
        for (const node of srcTree) {
            const newIdx = (node.pageIndex >= 0) ? toMergedIdx(node.pageIndex) : null;
            const children = remapOutlineTree(node.children || [], toMergedIdx);
            if (newIdx != null && node.title) {
                out.push({ title: node.title, pageIdx: newIdx, children });
            } else {
                for (const c of children) out.push(c);
            }
        }
        return out;
    }

    /**
     * Recursively attach a hierarchical outline to a doc. tree shape is
     * [{title, pageIdx, children}]. Builds /Outlines + per-item dicts by
     * hand (pdf-lib has no public outline API).
     */
    function attachOutline(doc, tree) {
        if (!tree.length) return;
        const ctx = doc.context;
        const pages = doc.getPages();
        const rootRef = ctx.nextRef();

        function buildLevel(nodes, parentRef) {
            if (!nodes.length) return { firstRef: null, lastRef: null, openCount: 0 };
            const refs = nodes.map(() => ctx.nextRef());
            let descendantCount = 0;
            nodes.forEach((node, i) => {
                const targetPage = pages[Math.min(Math.max(node.pageIdx, 0), pages.length - 1)];
                const dest = PL.PDFArray.withContext(ctx);
                dest.push(targetPage.ref);
                dest.push(PL.PDFName.of('Fit'));
                const dict = {
                    Title: PL.PDFString.of(node.title),
                    Parent: parentRef,
                    Dest: dest,
                };
                if (i > 0) dict.Prev = refs[i - 1];
                if (i < nodes.length - 1) dict.Next = refs[i + 1];
                const childResult = buildLevel(node.children || [], refs[i]);
                if (childResult.firstRef) {
                    dict.First = childResult.firstRef;
                    dict.Last = childResult.lastRef;
                    dict.Count = node.children.length;
                    descendantCount += 1 + childResult.openCount;
                } else {
                    descendantCount += 1;
                }
                ctx.assign(refs[i], ctx.obj(dict));
            });
            return { firstRef: refs[0], lastRef: refs[refs.length - 1], openCount: descendantCount };
        }

        const built = buildLevel(tree, rootRef);
        const rootDict = { Type: PL.PDFName.of('Outlines'), Count: tree.length };
        if (built.firstRef) { rootDict.First = built.firstRef; rootDict.Last = built.lastRef; }
        ctx.assign(rootRef, ctx.obj(rootDict));
        doc.catalog.set(PL.PDFName.of('Outlines'), rootRef);
    }

    /**
     * Read a /PageLabels NumberTree (flat Nums-array form only).
     * Returns [{ pageIndex, dict: { S, St?, P? } }].
     */
    function readPageLabels(srcDoc) {
        const out = [];
        let ref;
        try { ref = srcDoc.catalog.get(PL.PDFName.of('PageLabels')); } catch { return out; }
        if (!ref) return out;
        const ctx = srcDoc.context;
        let tree;
        try { tree = ref.entries ? ref : ctx.lookup(ref); } catch { return out; }
        const nums = tree.get(PL.PDFName.of('Nums'));
        if (!nums || !nums.array) return out;
        for (let i = 0; i + 1 < nums.array.length; i += 2) {
            const idxObj = nums.array[i];
            const dictRefOrVal = nums.array[i + 1];
            const pageIndex = Number(idxObj.numberValue != null ? idxObj.numberValue : String(idxObj));
            if (!Number.isFinite(pageIndex) || pageIndex < 0) continue;
            let labelDict;
            try { labelDict = dictRefOrVal.entries ? dictRefOrVal : ctx.lookup(dictRefOrVal); } catch { continue; }
            const dict = {};
            const sObj = labelDict.get(PL.PDFName.of('S'));
            if (sObj) {
                const s = sObj.encodedName ? sObj.encodedName.replace(/^\//, '') : stripLeadingSlash(String(sObj));
                if (s) dict.S = s;
            }
            const stObj = labelDict.get(PL.PDFName.of('St'));
            if (stObj) {
                const st = Number(stObj.numberValue != null ? stObj.numberValue : String(stObj));
                if (Number.isFinite(st)) dict.St = st;
            }
            const pObj = labelDict.get(PL.PDFName.of('P'));
            if (pObj) {
                dict.P = pObj.decodeText ? pObj.decodeText() : String(pObj);
            }
            out.push({ pageIndex, dict });
        }
        return out;
    }

    /**
     * Write a /PageLabels NumberTree onto a doc. `entries` is a list of
     * { pageIdx, dict: { S, St?, P? } }. Will be sorted + deduplicated.
     */
    function attachPageLabels(doc, entries) {
        if (!entries.length) return;
        const ctx = doc.context;
        const sorted = [...entries].sort((a, b) => a.pageIdx - b.pageIdx);
        const dedup = [];
        for (const e of sorted) {
            if (dedup.length > 0 && dedup[dedup.length - 1].pageIdx === e.pageIdx) {
                dedup[dedup.length - 1] = e;
            } else {
                dedup.push(e);
            }
        }
        const nums = PL.PDFArray.withContext(ctx);
        for (const e of dedup) {
            nums.push(PL.PDFNumber.of(e.pageIdx));
            const dict = {};
            if (e.dict.S) dict.S = PL.PDFName.of(e.dict.S);
            if (e.dict.St != null) dict.St = PL.PDFNumber.of(e.dict.St);
            if (e.dict.P) dict.P = PL.PDFString.of(e.dict.P);
            nums.push(ctx.obj(dict));
        }
        const treeDict = ctx.obj({ Nums: nums });
        const treeRef = ctx.register(treeDict);
        doc.catalog.set(PL.PDFName.of('PageLabels'), treeRef);
    }

    Object.assign(global.PdfDocHelpers, {
        readSourceOutlines,
        remapOutlineTree,
        attachOutline,
        readPageLabels,
        attachPageLabels,
    });
})(typeof self !== 'undefined' ? self : globalThis);
